import classNames from "classnames";
import React, { FC, useEffect, useState } from "react";
import { LuCreditCard, LuExternalLink, LuUsers } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import { LottieLoading } from "../../components/graphics/LottieLoading";
import {
  useChangeIntervalMutation,
  useGetSeasonalQuery,
  useGetSubscriptionPlanQuery,
} from "../../slices/SubscriptionSlice";
import { GetPageTitle } from "../../support/ScrollToTop";
import {
  cardLine,
  formatDay,
  formatMoney,
  intervalWord,
  invoiceStatusLabel,
  nextBillLine,
  phasePill,
  priceLine,
  seasonalPlanLine,
  taxNote,
  yearlySaving,
} from "./subscription.utils";
import {
  currentOrgId,
  useBillingRedirects,
  useSubscriptionStatus,
} from "./useSubscription";

const CARD = "rounded-[16px] border border-[#EDEFF3] bg-white p-5 max-sm:p-4";
const LABEL = "text-[12px] font-bold uppercase tracking-wide text-[#8A90A2]";

const PILL_TONES = {
  purple: "bg-[#F3F0FF] text-[#5A3EEB]",
  green: "bg-[#E8F7EE] text-[#1E7B45]",
  orange: "bg-[#FFF1E2] text-[#A6520B]",
  red: "bg-[#FDECEC] text-[#B42318]",
  gray: "bg-[#F1F2F5] text-[#5B6478]",
};

/**
 * Settings > Subscription (seat billing, owner-approved mockup 2026-10-24; its
 * own Settings tab since 2026-10-25, was Settings > Billing > Plan & seats).
 * Only for people with "Manage subscription & payment" (the server refuses
 * everyone else). Everything shown is read from Stripe.
 */
export const PlanAndSeatsPage: FC = () => {
  const navigate = useNavigate();
  const orgId = currentOrgId();
  const { status, isLoading: isLoadingStatus } = useSubscriptionStatus(orgId);
  const canManage = !!status?.canManage;
  const {
    data: plan,
    isLoading,
    error,
    refetch,
  } = useGetSubscriptionPlanQuery({ orgId }, { skip: !orgId || !canManage });
  const { data: seasonalInfo } = useGetSeasonalQuery(
    { orgId },
    { skip: !orgId || !canManage },
  );
  const seasonalLine = seasonalPlanLine(seasonalInfo);
  const [changeInterval, intervalState] = useChangeIntervalMutation();
  const {
    addPaymentMethod,
    openPortal,
    busy,
    error: redirectError,
  } = useBillingRedirects(orgId);
  const [intervalError, setIntervalError] = useState("");

  useEffect(() => {
    document.title = GetPageTitle("Subscription");
  }, []);

  // Back from the Stripe portal: show the new card / plan.
  useEffect(() => {
    if (
      new URLSearchParams(window.location.search).get("from") === "portal" &&
      canManage
    )
      void refetch();
  }, [canManage, refetch]);

  if (isLoadingStatus || (canManage && isLoading)) return <LottieLoading />;

  if (!status?.seatBilling || !canManage) {
    return (
      <div className={"py-10 text-center font-Manrope"}>
        <div className={"text-[16px] font-bold text-[#1F2433]"}>
          {status?.seatBilling
            ? "You don't have access to the firm's plan"
            : "Your firm's plan isn't billed per user"}
        </div>
        <p className={"mt-2 text-[13.5px] text-[#6B7185]"}>
          {status?.seatBilling
            ? `Ask ${
                status.billingContactName || "your firm's billing contact"
              } or an Admin to give you "Manage subscription & payment".`
            : "Contact support@procharted.com with any question about your plan."}
        </p>
        <button
          type={"button"}
          onClick={() => navigate("/home")}
          className={
            "mt-4 text-[13px] font-bold text-[#6B4EFF] hover:underline"
          }>
          Back to Home
        </button>
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div
        role={"alert"}
        className={"py-10 text-center text-[14px] text-[#B42318]"}>
        {typeof error === "string"
          ? error
          : "The plan couldn't be loaded. Please try again."}
      </div>
    );
  }

  const pill = phasePill(plan.phase);
  const interval = intervalWord(plan.interval);
  const saving = yearlySaving(plan.monthlyUnitAmount, plan.yearlyUnitAmount);
  const card = cardLine(plan.cardBrand, plan.cardLast4);
  const inTrial = plan.status === "trialing";

  const switchTo = async (next: "month" | "year") => {
    if (next === plan.interval) return;
    setIntervalError("");
    try {
      await changeInterval({ orgId, interval: next }).unwrap();
    } catch (e) {
      setIntervalError(
        typeof e === "string"
          ? e
          : "The plan couldn't be changed. Please try again.",
      );
    }
  };
  const canSwitch = (next: "month" | "year") =>
    next === "year" ? plan.canSwitchToYearly : plan.canSwitchToMonthly;

  return (
    <div className={"max-w-[880px] py-6 font-Manrope"}>
      <div className={"mb-5 flex flex-wrap items-center gap-3"}>
        <h1 className={"text-2xl font-semibold text-[#1F2433]"}>
          Subscription
        </h1>
        <span
          data-testid={"plan-status"}
          className={classNames(
            "rounded-full px-3 py-1 text-[12px] font-bold",
            PILL_TONES[pill.tone],
          )}>
          {pill.label}
        </span>
      </div>

      {(inTrial || plan.phase === "grace" || plan.phase === "locked") &&
        plan.trialEndsAt && (
          <div
            className={
              "mb-4 rounded-[14px] border border-[#DDD5FF] bg-[#F7F5FF] px-4 py-3 text-[13.5px] font-semibold text-[#3A2BA0]"
            }>
            {inTrial
              ? `Free trial until ${formatDay(
                  plan.trialEndsAt,
                )}. Unlimited users while you try ProCharted; billing starts after the trial${
                  card ? "" : " once a card is added"
                }.`
              : `Your free trial ended on ${formatDay(
                  plan.trialEndsAt,
                )}. Add a payment method to keep using ProCharted.`}
          </div>
        )}

      <div className={"grid grid-cols-2 gap-4 max-md:grid-cols-1"}>
        {/* Plan */}
        <section className={CARD} aria-label={"Plan"}>
          <div className={LABEL}>Plan</div>
          <div className={"mt-1 text-[17px] font-extrabold text-[#1F2433]"}>
            ProCharted — per user
          </div>
          <div className={"mt-1 text-[13.5px] text-[#4A5168]"}>
            {priceLine(plan.unitAmount, interval, plan.currency)}
          </div>
          {plan.discountName || plan.percentOff || plan.amountOff ? (
            <div
              className={
                "mt-2 inline-block rounded-full bg-[#E8F7EE] px-2.5 py-0.5 text-[12px] font-bold text-[#1E7B45]"
              }>
              {plan.discountName || "Special price"}
              {plan.percentOff ? ` · ${plan.percentOff}% off` : ""}
              {plan.amountOff
                ? ` · ${formatMoney(plan.amountOff, plan.currency)} off`
                : ""}
            </div>
          ) : null}

          {plan.standardPrice && (
            <div className={"mt-4"}>
              <div
                role={"radiogroup"}
                aria-label={"Billing period"}
                className={
                  "inline-flex rounded-[10px] border border-[#DCE2EA] bg-[#F8F9FB] p-1"
                }>
                {(["month", "year"] as const).map((option) => {
                  const selected = interval === option;
                  const disabled =
                    intervalState.isLoading ||
                    (!selected && !canSwitch(option));
                  return (
                    <button
                      key={option}
                      type={"button"}
                      role={"radio"}
                      aria-checked={selected}
                      disabled={disabled}
                      onClick={() => void switchTo(option)}
                      className={classNames(
                        "h-[32px] rounded-[8px] px-4 text-[13px] font-bold",
                        selected
                          ? "bg-white text-[#1F2433] shadow-[0_1px_3px_rgba(16,24,40,0.12)]"
                          : "text-[#6B7185]",
                        disabled &&
                          !selected &&
                          "cursor-not-allowed opacity-50",
                      )}>
                      {option === "month" ? "Monthly" : "Yearly"}
                    </button>
                  );
                })}
              </div>
              {saving && (
                <div
                  className={"mt-2 text-[12.5px] font-semibold text-[#1E7B45]"}>
                  Yearly saves {formatMoney(saving, plan.currency)} per user per
                  year.
                </div>
              )}
              {interval === "year" && !plan.canSwitchToMonthly && (
                <div className={"mt-1 text-[12px] text-[#8A90A2]"}>
                  To switch back to monthly, contact support@procharted.com.
                </div>
              )}
              {!inTrial && interval === "month" && plan.canSwitchToYearly && (
                <div className={"mt-1 text-[12px] text-[#8A90A2]"}>
                  Switching to yearly charges the year today, minus the unused
                  part of this month.
                </div>
              )}
              {intervalError && (
                <div
                  role={"alert"}
                  className={"mt-2 text-[12.5px] font-bold text-[#B42318]"}>
                  {intervalError}
                </div>
              )}
            </div>
          )}
        </section>

        {/* Seats + next bill */}
        <section className={CARD} aria-label={"Seats"}>
          <div className={"flex items-start justify-between gap-3"}>
            <div>
              <div className={LABEL}>Paid seats</div>
              <div
                className={
                  "mt-1 flex items-center gap-2 text-[26px] font-extrabold text-[#1F2433]"
                }>
                <LuUsers size={20} className={"text-[#6B4EFF]"} />
                {plan.seats}
              </div>
              <div className={"text-[12.5px] text-[#8A90A2]"}>
                {plan.activePeople} active{" "}
                {plan.activePeople === 1 ? "person" : "people"} in your team
                {seasonalInfo?.userIds.length ? " (not counting seasonal)" : ""}
              </div>
              {seasonalLine && (
                <div
                  className={"mt-0.5 text-[12.5px] text-[#2E9E6A]"}
                  data-testid={"seasonal-line"}>
                  {seasonalLine}
                </div>
              )}
            </div>
            <div className={"text-right"}>
              <div className={LABEL}>
                {interval === "year" ? "Per year" : "Per month"}
              </div>
              <div className={"mt-1 text-[20px] font-extrabold text-[#1F2433]"}>
                {formatMoney(plan.periodTotal, plan.currency)}
              </div>
              <div className={"text-[12px] text-[#8A90A2]"}>
                {plan.taxInclusive
                  ? taxNote(true, plan.currency)
                  : "before tax"}
              </div>
            </div>
          </div>
          <div className={"mt-4 border-t border-[#EDEFF3] pt-3"}>
            <div className={LABEL}>Next bill</div>
            <div className={"mt-1 text-[14px] font-semibold text-[#1F2433]"}>
              {nextBillLine(plan)}
            </div>
            <div className={"mt-1 text-[12px] text-[#8A90A2]"}>
              A seat is added when an invited person accepts (charged for the
              days left in the period). Removing someone lowers the next bill.
            </div>
          </div>
        </section>
      </div>

      {/* Payment method */}
      <section
        className={classNames(CARD, "mt-4")}
        aria-label={"Payment method"}>
        <div className={"flex flex-wrap items-center justify-between gap-3"}>
          <div className={"flex items-center gap-3"}>
            <span
              className={
                "flex h-10 w-10 items-center justify-center rounded-[10px] bg-[#F1F2F5] text-[#5B6478]"
              }>
              <LuCreditCard size={18} />
            </span>
            <div>
              <div className={LABEL}>Payment method</div>
              <div className={"text-[14.5px] font-bold text-[#1F2433]"}>
                {card || "No card yet"}
              </div>
            </div>
          </div>
          <button
            type={"button"}
            disabled={busy}
            onClick={() => void (card ? openPortal() : addPaymentMethod())}
            className={
              card
                ? "h-[36px] rounded-lg border border-[#DCE2EA] bg-white px-4 text-[13px] font-bold text-[#3A4256] hover:bg-[#F8F9FB] disabled:opacity-60"
                : "h-[36px] rounded-lg bg-[#6B4EFF] px-4 text-[13px] font-bold text-white hover:bg-[#5A3EEB] disabled:opacity-60"
            }>
            {busy ? "Opening Stripe…" : card ? "Change" : "Add payment method"}
          </button>
        </div>
        <div className={"mt-2 text-[12px] text-[#8A90A2]"}>
          Cards are entered and changed on Stripe&apos;s secure pages;
          ProCharted only sees the brand and last 4 digits.
        </div>
        {redirectError && (
          <div
            role={"alert"}
            className={"mt-2 text-[12.5px] font-bold text-[#B42318]"}>
            {redirectError}
          </div>
        )}
      </section>

      {/* Past charges */}
      <section className={classNames(CARD, "mt-4")} aria-label={"Past charges"}>
        <div className={"mb-3 text-[15px] font-extrabold text-[#1F2433]"}>
          Past charges
        </div>
        {plan.invoices.length === 0 ? (
          <div className={"py-4 text-center text-[13px] text-[#8A90A2]"}>
            No charges yet.
          </div>
        ) : (
          <div className={"overflow-x-auto"}>
            <table className={"w-full min-w-[520px] text-left text-[13px]"}>
              <thead>
                <tr className={"border-b border-[#EDEFF3] text-[#8A90A2]"}>
                  <th className={"py-2 font-semibold"}>Date</th>
                  <th className={"py-2 font-semibold"}>Description</th>
                  <th className={"py-2 text-right font-semibold"}>Amount</th>
                  <th className={"py-2 pl-4 font-semibold"}>Status</th>
                  <th className={"py-2"} />
                </tr>
              </thead>
              <tbody>
                {plan.invoices.map((invoice) => {
                  const paid = invoice.status === "paid";
                  return (
                    <tr
                      key={invoice.id}
                      className={"border-b border-[#F1F2F5] last:border-b-0"}>
                      <td className={"py-2.5 text-[#1F2433]"}>
                        {formatDay(invoice.date)}
                      </td>
                      <td className={"py-2.5 text-[#4A5168]"}>
                        {invoice.description || invoice.number || "ProCharted"}
                      </td>
                      <td
                        className={
                          "py-2.5 text-right font-semibold text-[#1F2433]"
                        }>
                        {formatMoney(invoice.total, invoice.currency, true)}
                      </td>
                      <td className={"py-2.5 pl-4"}>
                        <span
                          className={classNames(
                            "rounded-full px-2 py-0.5 text-[11.5px] font-bold",
                            paid ? PILL_TONES.green : PILL_TONES.orange,
                          )}>
                          {invoiceStatusLabel(invoice.status)}
                        </span>
                      </td>
                      <td className={"py-2.5 text-right"}>
                        {invoice.receiptUrl && (
                          <a
                            href={invoice.receiptUrl}
                            target={"_blank"}
                            rel={"noreferrer"}
                            className={
                              "inline-flex items-center gap-1 font-bold text-[#6B4EFF] hover:underline"
                            }>
                            Receipt <LuExternalLink size={12} />
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {plan.billingContactName && (
        <p className={"mt-3 text-[12.5px] text-[#8A90A2]"}>
          Billing contact: {plan.billingContactName}. Receipts are emailed by
          Stripe.
        </p>
      )}
    </div>
  );
};
